import React, { Suspense } from "react";
import { useContext } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import CustomLoader from "./components/CustomLoader/CustomLoader";
import DefaultRoute from "./routes/Components/DefaultRoute";
import Header from "./components/Header/Header";
import Login from "./containers/Login/Login";
import PrivateRoute from "./routes/Components/PrivateRoute";
import PublicRoute from "./routes/Components/PublicRoute";
import Sidebar from "./containers/Sidebar/Sidebar";
import useAuthorizeNetScript from "./CustomHooks/useAuthorizeNetScript.js";
import { AuthContext } from "./globalContext/auth/authProvider";
import { UserContext } from "./globalContext/user/userProvider";
import navigations from "./routes/routeNames";
import UsersList from "./containers/UsersList/UsersList";
import { Roles } from "./consts/constants";

const ForgotPassword = React.lazy(
  () => import("./containers/ForgotPassword/ForgotPassword"),
);
const DentistSignup = React.lazy(
  () => import("./containers/DentistSignup/DentistSignup"),
);
const SaaSList = React.lazy(() => import("./containers/SaaSList/SaaSList"));
const CreateSaaS = React.lazy(
  () => import("./containers/CreateSaaS/CreateSaaS"),
);
const PurchaseOnlinePrograms = React.lazy(
  () =>
    import("./containers/PurchaseOnlinePrograms/PurchaseOnlinePrograms.jsx"),
);
const Subscriptions = React.lazy(
  () => import("./containers/Subscriptions/Subscriptions.jsx"),
);
const OnlineProgramTestAttempts = React.lazy(
  () =>
    import("./containers/OnlineProgramTestAttempts/OnlineProgramTestAttempts.jsx"),
);
const ResetPassword = React.lazy(
  () => import("./containers/ResetPassword/ResetPassword"),
);
const CreateUsers = React.lazy(
  () => import("./containers/CreateUsers/CreateUsers"),
);
const AccountList = React.lazy(
  () => import("./containers/AccountList/AccountList"),
);

const CreateAccount = React.lazy(
  () => import("./containers/CreateAccount/CreateAccount"),
);
const CoursesList = React.lazy(
  () => import("./containers/Courses/CoursesList"),
);
const CreateCourse = React.lazy(
  () => import("./containers/CreateCourse/CreateCourse"),
);
const Category = React.lazy(() => import("./containers/Category/Category"));
const DentistCategories = React.lazy(
  () => import("./containers/DentistCategories/DentistCategoryies.jsx"),
);

const DentistAccount = React.lazy(
  () => import("./containers/DentistAccount/DentistAccount"),
);
const DentistSubCategory = React.lazy(
  () => import("./containers/DentistSubCategory/DentistSubCategory"),
);
const DentistCourses = React.lazy(
  () => import("./containers/DentistCourses/DentistCourses"),
);

const SpeakersPage = React.lazy(
  () => import("./containers/SpeakersPage/SpeakersPage"),
);

function App() {
  const [authState] = useContext(AuthContext);
  const [userState] = useContext(UserContext);

  useAuthorizeNetScript();

  return (
    <Router>
      {!userState?.isGettingUserDetails && !!authState.auth && <Header />}
      <main className="main">
        {!!authState.auth &&
          !userState?.isGettingUserDetails &&
          authState?.role === Roles.admin && <Sidebar />}
        <Suspense fallback={<CustomLoader />}>
          <Routes>
            <Route path={navigations.ROOT} element={<DefaultRoute />} />
            <Route
              path={navigations.LOGIN}
              element={<PublicRoute component={<Login />} />}
            />
            <Route
              path={navigations.SIGNUP}
              element={<PublicRoute component={<DentistSignup />} />}
            />
            <Route
              path={navigations.FORGOT_PASSWORD}
              element={<PublicRoute component={<ForgotPassword />} />}
            />
            <Route
              path={navigations.RESET_PASSWORD}
              element={<PublicRoute component={<ResetPassword />} />}
            />
            <Route
              path={navigations.ACCOUNTS}
              element={<PrivateRoute component={<AccountList />} />}
            />
            <Route
              path={navigations.SAAS}
              element={<PrivateRoute component={<SaaSList />} />}
            />
            <Route
              path={navigations.USERS_TEST_ATTEMPTS}
              element={
                <PrivateRoute component={<OnlineProgramTestAttempts />} />
              }
            />
            <Route
              path={navigations.CREATE_SAAS}
              element={<PrivateRoute component={<CreateSaaS />} />}
            />
            <Route
              path={navigations.USERS}
              element={<PrivateRoute component={<UsersList />} />}
            />
            <Route
              path={navigations.CREATE_USER}
              element={<PrivateRoute component={<CreateUsers />} />}
            />
            <Route path={navigations.OUT_SOURCED} element={<DefaultRoute />} />
            <Route
              path={navigations.CREATE_ACCOUNT}
              element={<PrivateRoute component={<CreateAccount />} />}
            />

            <Route
              path={navigations.CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.CREATE_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.ONLINE_PROGRAM}
              element={<PrivateRoute component={<CoursesList />} />}
            />
            <Route
              path={navigations.ONLINE_PROGRAM_CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.ONLINE_PROGRAM_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.LIVE_LECTURES}
              element={<PrivateRoute component={<CoursesList />} />}
            />
            <Route
              path={navigations.LIVE_LECTURES_CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.LIVE_LECTURES_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.CADAVER_PROGRAM}
              element={<PrivateRoute component={<CoursesList />} />}
            />
            <Route
              path={navigations.CADAVER_PROGRAM_CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.CADAVER_PROGRAM_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.LIVE_EXTERNSHIP}
              element={<PrivateRoute component={<CoursesList />} />}
            />
            <Route
              path={navigations.LIVE_EXTERNSHIP_CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.LIVE_EXTERNSHIP_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.LIVE_EXTERNSHIP_OBSERVATION}
              element={<PrivateRoute component={<CoursesList />} />}
            />
            <Route
              path={navigations.LIVE_EXTERNSHIP_OBSERVATION_CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.LIVE_EXTERNSHIP_OBSERVATION_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.LIVE_EXTERNSHIP_DENTAL_ASSISTANT}
              element={<PrivateRoute component={<CoursesList />} />}
            />
            <Route
              path={navigations.LIVE_EXTERNSHIP_DENTAL_ASSISTANT_CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.LIVE_EXTERNSHIP_DENTAL_ASSISTANT_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.MASTER_OF_ORAL_IMPLANTOLOGY}
              element={<PrivateRoute component={<CoursesList />} />}
            />
            <Route
              path={navigations.MASTER_OF_ORAL_IMPLANTOLOGY_CREATE_COURSE}
              element={<PrivateRoute component={<CreateCourse />} />}
            />
            <Route
              path={navigations.MASTER_OF_ORAL_IMPLANTOLOGY_CATEGORY}
              element={<PrivateRoute component={<Category />} />}
            />

            <Route
              path={navigations.HOME}
              element={<PrivateRoute component={<DentistCategories />} />}
            />
            <Route
              path={navigations.CATEGORY}
              element={<PrivateRoute component={<DentistSubCategory />} />}
            />
            <Route
              path={navigations.GROUP}
              element={<PrivateRoute component={<DentistSubCategory />} />}
            />
            <Route
              path={navigations.GROUP_CATEGORY}
              element={<PrivateRoute component={<DentistSubCategory />} />}
            />
            <Route
              path={navigations.GROUP_SUBCATEGORY}
              element={<PrivateRoute component={<DentistCourses />} />}
            />
            <Route
              path={navigations.DENTIST_SETTING}
              element={<PrivateRoute component={<DentistAccount />} />}
            />
            <Route
              path={navigations.SUBCATEGORY}
              element={<PrivateRoute component={<DentistCourses />} />}
            />
            <Route
              path={navigations.CATEGORY_COURSES}
              element={<PrivateRoute component={<DentistCourses />} />}
            />
            <Route
              path={navigations.PURCHASE_ONLINE_PROGRAMS}
              element={<PrivateRoute component={<PurchaseOnlinePrograms />} />}
            />
            <Route
              path={navigations.SUBSCRIPTIONS}
              element={<PrivateRoute component={<Subscriptions />} />}
            />

            <Route
              path={navigations.SPEAKERS}
              element={<PrivateRoute component={<SpeakersPage />} />}
            />
          </Routes>
        </Suspense>
      </main>
    </Router>
  );
}

export default App;
